import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { resetPreferenceOverride, updatePreferenceOverride } from './preferencesV2Actions';
import { getPreferenceObj, getPreferenceValue } from './preferencesV2Selectors';

interface undoProps {
  userMessage?: string;
  disableUndoAction?: boolean;
}

// note - Please use this hook whenever using preferences v2. You might confuse yourself if you do not. See some of the comments below
export const usePreferenceV2 = (preferenceName: string, defaultValue?: any) => {
  const dispatch = useDispatch();
  const preference = useSelector((state) => getPreferenceObj(state, preferenceName));
  const preferenceValue = useSelector((state) => getPreferenceValue(state, preferenceName, defaultValue));

  // updatePreference has id as the name in order to do an optimistic update. We want to key off of preference name bc it is stable
  const updatePreference = useCallback(
    (value: any, undoOptions?: undoProps) => {
      dispatch(
        updatePreferenceOverride(
          {
            preferenceName,
            value,
            preferenceId: preference?.preferenceId,
            id: preferenceName,
            dataType: preference?.dataType,
          },
          undoOptions && { undo: undoOptions },
        ),
      );
    },
    [dispatch, preferenceName, preference?.preferenceId, preference?.dataType],
  );

  // resetPreference resets the preference to the default value and removes any overrides.
  const resetPreference = useCallback(
    () => dispatch(resetPreferenceOverride({ id: preferenceName })),
    [dispatch, preferenceName],
  );

  return [preferenceValue, updatePreference, resetPreference];
};

export const useUpdatePreferenceV2 = (preferenceName: string) => {
  const dispatch = useDispatch();
  const preference = useSelector((state) => getPreferenceObj(state, preferenceName));

  // updatePreference has id as the name in order to do an optimistic update. We want to key off of preference name bc it is stable
  const updatePreference = useCallback(
    (value: any, undoOptions?: undoProps) =>
      dispatch(
        updatePreferenceOverride(
          {
            preferenceName,
            value,
            preferenceId: preference?.preferenceId,
            id: preferenceName,
            dataType: preference?.dataType,
          },
          undoOptions && { undo: undoOptions },
        ),
      ),
    [dispatch, preferenceName, preference?.preferenceId, preference?.dataType],
  );

  return [updatePreference];
};

export const useResetPreferenceV2 = (preferenceName: string, defaultValue?: any) => {
  const dispatch = useDispatch();

  // updatePreference has id as the name in order to do an optimistic update. We want to key off of preference name bc it is stable
  const resetPreference = useCallback(
    () =>
      dispatch(
        resetPreferenceOverride({
          id: preferenceName,
          defaultValue,
        }),
      ),
    [dispatch, preferenceName, defaultValue],
  );

  return [resetPreference];
};

export default usePreferenceV2;
